import { Blueprint, ChartBar, IconProps } from '@phosphor-icons/react'
import { store } from 'store/index'

/**
 * The two products this app ships, and how to tell which one you are in.
 *
 * Single source of truth: the switcher, the sidebar and anything else that
 * needs to know "which product is this" all read from here, rather than each
 * keeping its own copy of the labels, paths and access rules.
 *
 * "Job Architecture" is the new name for the career frameworks and is deliberately
 * used only in product-level chrome for now — the rest of the app still says
 * framework.
 */
export type ProductId = 'job-architecture' | 'wfi'

export type Product = {
  id: ProductId
  label: string
  icon: React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
  >
  /** Where selecting this product takes you. */
  href: () => string
  /** Whether this user can reach it at all. */
  available: () => boolean
}

export const WFI_PATH = '/wfi'

/**
 * The app home, which is the product's own shell — the sidebar, the nav and
 * everything Job Architecture is.
 *
 * It used to point at the reader's team framework (`/teams/:slug/framework`).
 * That page is the shareable public view of a framework and renders without the
 * app chrome, so switching products landed on a page with no sidebar and no way
 * back — verified on a plain reload, not just through the switcher. `/` is also
 * where WFI's own "Back to main app" goes, so the two ways across now agree.
 */
const jobArchitectureHref = (): string => '/'

export const PRODUCTS: Product[] = [
  {
    id: 'wfi',
    label: 'Workforce Intelligence',
    icon: ChartBar,
    href: () => WFI_PATH,
    // Both flags plus org admin, matching User#wfi_access? on the Ruby side.
    available: () => {
      const user = store.currentUser

      return !!user?.isAdmin && !!user?.wfiEnabled && !!user?.org?.wfiEnabled
    },
  },
  {
    id: 'job-architecture',
    label: 'Job Architecture',
    icon: Blueprint,
    href: jobArchitectureHref,
    available: () => true,
  },
]

export const productById = (id: ProductId): Product => {
  const product = PRODUCTS.find((p) => p.id === id)

  if (!product) throw new Error(`Unknown product: ${id}`)

  return product
}

/**
 * Derived from the URL rather than passed around: Rails serves the WFI shell
 * for every /wfi/* path, and the main app for everything else, so the path is
 * already the authority on which product you are looking at.
 */
export const currentProductId = (
  pathname: string = window.location.pathname,
): ProductId => (pathname.startsWith(WFI_PATH) ? 'wfi' : 'job-architecture')
